import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Chip, IconButton, Snackbar, Stack, Tooltip, Typography } from '@mui/material';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

import { applicationsApi, type ApplicationEditableFieldDto } from '../../api/admin';
import {
  editErrorMessage,
  editedByLine,
  renderValue,
  requiresReload,
} from '../../lib/applicationFieldEditing';
import { ApplicationFieldEditDialog } from './ApplicationFieldEditDialog';

/**
 * Shared query for the editable-field descriptor.
 *
 * Every inline edit point on every tab reads this one key, so a correction made
 * on Overview is visible on Employer without a refetch, and one save invalidates
 * them all together.
 */
export function useEditableFields(applicationId: string) {
  return useQuery({
    queryKey: ['applicationEditableFields', applicationId],
    queryFn: () => applicationsApi.editableFields(applicationId),
    enabled: !!applicationId,
    retry: false,
    staleTime: 15_000,
  });
}

interface EditingContextValue {
  applicationId: string;
  applicationStatus: string;
  openEditor: (field: ApplicationEditableFieldDto) => void;
}

const EditingContext = createContext<EditingContextValue | null>(null);

/**
 * Owns the single edit dialog for the whole application detail page.
 *
 * The dialog used to be rendered by each `EditableValue`, which meant it lived
 * inside whichever tab you happened to be on — switching tabs unmounted the tab,
 * and the dialog and the reviewer's half-typed reason went with it. A modal
 * disappearing because something *behind* it changed is a bug, not a quirk.
 *
 * Hoisting it here means one dialog for the page, outliving any single tab.
 */
export function ApplicationEditingProvider({
  applicationId,
  applicationStatus,
  children,
}: {
  applicationId: string;
  applicationStatus: string;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const fieldsQ = useEditableFields(applicationId);

  const [editing, setEditing] = useState<ApplicationEditableFieldDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mustReload, setMustReload] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const openEditor = useCallback((field: ApplicationEditableFieldDto) => {
    setError(null);
    setMustReload(false);
    setEditing(field);
  }, []);

  const save = useMutation({
    mutationFn: (input: { value: string; reasonCode: string; reasonDetail: string }) => {
      if (!editing) throw new Error('No field is being edited.');
      return applicationsApi.editFields(applicationId, {
        expectedStatus: applicationStatus,
        reasonCode: input.reasonCode,
        reasonDetail: input.reasonDetail,
        changes: [
          {
            fieldKey: editing.key,
            value: input.value,
            // Echoed so a concurrent edit by someone else is refused rather than
            // silently overwritten.
            lastChangeId: editing.lastChangeId ?? null,
          },
        ],
      });
    },
    onSuccess: (result) => {
      setEditing(null);
      setError(null);
      setMustReload(false);
      setToast(
        result.changed.length === 0
          ? 'That value already matched — nothing was changed.'
          : result.sideEffects.length
            ? `Saved. Also cleared: ${result.sideEffects.join(', ')}.`
            : 'Saved.',
      );
      void queryClient.invalidateQueries({ queryKey: ['applicationEditableFields', applicationId] });
      void queryClient.invalidateQueries({ queryKey: ['applicationChanges', applicationId] });
      void queryClient.invalidateQueries({ queryKey: ['applicationJourney', applicationId] });
    },
    onError: (e) => {
      // Kept open on failure: the reviewer's typing is the expensive part, and a
      // 409 needs them to read the conflict before deciding.
      setError(editErrorMessage(e));
      setMustReload(requiresReload(e));
    },
  });

  const value = useMemo(
    () => ({ applicationId, applicationStatus, openEditor }),
    [applicationId, applicationStatus, openEditor],
  );

  return (
    <EditingContext.Provider value={value}>
      {children}

      <ApplicationFieldEditDialog
        open={!!editing}
        field={editing}
        reasonCodes={fieldsQ.data?.reasonCodes ?? []}
        saving={save.isPending}
        errorMessage={error}
        mustReload={mustReload}
        onClose={() => {
          setEditing(null);
          setError(null);
          setMustReload(false);
        }}
        onSubmit={(input) => save.mutate(input)}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </EditingContext.Provider>
  );
}

/**
 * The "edited" marker.
 *
 * Rendered independently of the pencil: a value corrected while an application was
 * in review must still read as corrected once it is funded, when nothing is
 * editable any more.
 */
export function EditedMarker({ field }: { field: ApplicationEditableFieldDto }) {
  if (!field.overridden && !field.lastChangedAt) return null;
  const provenance = editedByLine(field) ?? 'Changed by a reviewer';
  const source =
    field.overridden && field.sourceResolved !== false
      ? ` · ${field.targetService} holds ${renderValue(field.sourceValue, field.valueType)}`
      : '';
  return (
    <Tooltip title={`${provenance}${source}`} arrow>
      <Chip
        size="small"
        label="edited"
        color="warning"
        variant="outlined"
        sx={{
          height: 17,
          fontSize: '0.62rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          cursor: 'help',
        }}
      />
    </Tooltip>
  );
}

export interface EditableValueProps {
  /** Registry key, e.g. `personal.firstName`. Unknown keys render children unchanged. */
  fieldKey: string;
  /**
   * The tab's own formatting of the value. Inline editing decorates the existing
   * presentation rather than replacing it, so each tab keeps the shape it had.
   */
  children: ReactNode;
  /** Render the server's corrected value instead of `children` when one exists. */
  preferServerValue?: boolean;
  /** Only needed outside an ApplicationEditingProvider. */
  applicationId?: string;
  applicationStatus?: string;
}

/**
 * Wraps a rendered value with an inline edit affordance.
 *
 * The pencil appears ONLY when the field is editable — locked fields render
 * exactly as they did before, with no icon and no explanatory line. Repeating
 * "not editable while the application is withdrawn" under every field was
 * nine-tenths noise that buried the values; the absence of a pencil already says
 * it, and the Data & notes panel carries the reason once for anyone who wants it.
 */
/**
 * One warning per key per session. Without the guard a stale wrapper in a list
 * would log on every render and bury everything else in the console.
 */
const warnedFieldKeys = new Set<string>();

function warnOnceForUnknownFieldKey(fieldKey: string) {
  if (warnedFieldKeys.has(fieldKey)) return;
  warnedFieldKeys.add(fieldKey);
  console.warn(
    `[EditableValue] "${fieldKey}" is not in the server's editable-field registry, ` +
      `so this row renders read-only. Either the field was withdrawn and this wrapper ` +
      `should be removed, or the key is misspelled.`,
  );
}

export function EditableValue({
  fieldKey,
  children,
  preferServerValue = false,
  applicationId: applicationIdProp,
}: EditableValueProps) {
  const ctx = useContext(EditingContext);
  const applicationId = ctx?.applicationId ?? applicationIdProp ?? '';
  const fieldsQ = useEditableFields(applicationId);
  const field = fieldsQ.data?.fields.find((f) => f.key === fieldKey);

  // Unknown key, descriptor not loaded, or no provider: render the tab's own
  // value untouched rather than a placeholder.
  //
  // Failing soft is right at runtime but hides a stale `fieldKey` — a wrapper left
  // behind after a field is dropped from the server registry looks editable in the
  // source and does nothing on screen. Say so once in development, where the person
  // who can fix it is the one looking.
  if (import.meta.env.DEV && ctx && fieldsQ.data && !field) {
    warnOnceForUnknownFieldKey(fieldKey);
  }
  if (!field || !ctx) return <>{children}</>;

  const showServerValue = preferServerValue && field.overridden;

  return (
    <>
      <Stack direction="row" spacing={0.6} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box sx={{ minWidth: 0 }}>
          {showServerValue ? (
            <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.45 }}>
              {renderValue(field.currentValue, field.valueType)}
            </Typography>
          ) : (
            children
          )}
        </Box>

        <EditedMarker field={field} />

        {field.editable ? (
          <Tooltip title={`Edit ${field.label.toLowerCase()}`} arrow>
            <IconButton
              size="small"
              aria-label={`Edit ${field.label}`}
              onClick={() => ctx.openEditor(field)}
              sx={{ p: 0.25, opacity: 0.55, '&:hover': { opacity: 1 } }}
            >
              <EditRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>

      {/* The correction never replaces the evidence it shadows. */}
      {field.overridden && field.sourceResolved !== false && !showServerValue ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {field.targetService} holds {renderValue(field.sourceValue, field.valueType)}
        </Typography>
      ) : null}
    </>
  );
}
